export type PushPayloadData = {
  postId?: string;
  actorId?: string;
  followerId?: string;
  type?: string;
};

/** APNS system keys — not routing custom data. */
const APNS_SYSTEM_KEYS = new Set(['aps', 'pinpoint']);

function coerceString(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return undefined;
}

function parseMaybeJson(value: unknown): Record<string, unknown> | null {
  if (typeof value === 'string') {
    try {
      const parsed: unknown = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
  }

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  return null;
}

function routingFieldsFromSource(source: Record<string, unknown>): PushPayloadData {
  return {
    postId: coerceString(source.postId ?? source.post_id),
    actorId: coerceString(source.actorId ?? source.actor_id),
    followerId: coerceString(source.followerId ?? source.follower_id),
    type: coerceString(source.type),
  };
}

function hasRoutingFields(fields: PushPayloadData): boolean {
  return Boolean(fields.postId || fields.actorId || fields.followerId);
}

function extractTopLevelRoutingFields(rawData: Record<string, unknown>): PushPayloadData {
  const customData: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(rawData)) {
    if (!APNS_SYSTEM_KEYS.has(key)) {
      customData[key] = value;
    }
  }
  return routingFieldsFromSource(customData);
}

/** Legacy Pinpoint / pre-RawContent payload shapes still in Notification Center. */
function collectLegacyPayloadSources(rawData: Record<string, unknown>): Record<string, unknown>[] {
  const sources: Record<string, unknown>[] = [];

  const topJsonBody = parseMaybeJson(rawData.jsonBody);
  if (topJsonBody) sources.push(topJsonBody);

  const dataWrapper = parseMaybeJson(rawData.data);
  if (dataWrapper) {
    sources.push(dataWrapper);
    const dataJsonBody = parseMaybeJson(dataWrapper.jsonBody);
    if (dataJsonBody) sources.push(dataJsonBody);
  }

  const customData = parseMaybeJson(rawData.customData);
  if (customData) sources.push(customData);

  const pinpoint = parseMaybeJson(rawData.pinpoint);
  if (pinpoint) {
    sources.push(pinpoint);
    const pinpointJsonBody = parseMaybeJson(pinpoint.jsonBody);
    if (pinpointJsonBody) sources.push(pinpointJsonBody);
  }

  return sources;
}

export function unwrapPushPayloadData(
  rawData: Record<string, unknown> | undefined
): PushPayloadData {
  if (!rawData) return {};

  const topLevel = extractTopLevelRoutingFields(rawData);
  if (hasRoutingFields(topLevel)) {
    return topLevel;
  }

  for (const source of collectLegacyPayloadSources(rawData)) {
    const fields = routingFieldsFromSource(source);
    if (hasRoutingFields(fields)) {
      return fields;
    }
  }

  return {};
}

/**
 * Returns an in-app route path for a push notification payload, or null if unknown.
 *
 * Prod APNS (RawContent): camelCase `postId`, `actorId`, `followerId` as top-level userInfo keys
 * beside `aps`. No `deepLink` in push — route from IDs only.
 */
export function getRouteFromPushNotificationData(
  rawData: Record<string, unknown> | undefined
): string | null {
  const data = unwrapPushPayloadData(rawData);

  if (data.postId) {
    return `/post/${data.postId}`;
  }

  const profileId = data.followerId || data.actorId;
  if (profileId) {
    return `/profile/${profileId}`;
  }

  return null;
}
